import React, { useEffect, useState } from 'react';
import { compose } from 'recompose';
import styled, { StylesProps, cx, useStyles } from '../../../../../lib/styles';
import competitionVoteStyles from './CompetitionVoteBlock_styles';
import _ from 'lodash';
import Spacer from '../../../../../components/presentational/Spacer';
import { TrackingProfile, getProfileTracker } from '../../../../../lib/accountUtils';
import Modal from '../../../../../components/presentational/Modal';
import theme from '../../../../../lib/theme';
import Button from '../../../../../components/presentational/Button';
import { get, post } from '../../../../../lib/api';
import endpoints from '../../../../../lib/endpoints';
import dayjs from 'dayjs';
import { BootstrapAlert } from '../../../../../lib/notificationUtils';
import Video from '../../../../Article/Video';

const { font, colors, scale } = theme;

interface Vote {
	id: number,
	voter_email: string,
	competition_slug: string,
	entrant_email: string,
	created: string,
}
interface CompetitionEntry {
	entry_id: string;
	first_name: string;
	last_name: string;
	email: string;
	featured_image: string;
	video_link: string;
	youtube_video: string;
	description: string;
};
interface Competition {
	competition_id: string;
	name: string;
	description: string;
	total_votes_allowed: number;
	successful_vote_message: string;
	voting_limit_reached_message: string;
	voting_button_text: string;
	voting_closed_message: string;
	voting_open_date: string;
	voting_close_date: string;
	returnUrl: string;
	returnText: string;
};

export interface CompetitionVoteBlockData {
  type: 'competition_vote_block';
	competition: Competition,
  entries: CompetitionEntry[];
}

interface CompetitionVoteBlockProps {
  block: CompetitionVoteBlockData;
}

const CompetitionVoteBlock = ({
  block: {
		competition, 
		entries 
	},
  styles,
}: CompetitionVoteBlockProps & StylesProps<typeof competitionVoteStyles>) => {
	// console.log('CompetitionVoteBlock', competition, entries);
  const [showModal, setShowModal] = useState(false);
	const [selectedEntry, setSelectedEntry] = useState<CompetitionEntry | undefined>(undefined);

  const [isSaving, setIsSaving] = useState(false);
  const [wasValidated, setWasValidated] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState<boolean | undefined>(undefined);
  const [notificationMessage, setNotificationMessage] = useState<string>('');
	const [submitSuccessful, setSubmitSuccessful] = useState<boolean>(false);
	const [blockNotifications, setBlockNotifications] = useState<BootstrapAlert[]>([]);

  const [voterEmail, setVoterEmail] = useState<string | undefined>(undefined);
  const [voterFirstName, setVoterFirstName] = useState<string | undefined>(undefined);
  const [voterLastName, setVoterLastName] = useState<string | undefined>(undefined);
	const [voteHistory, setVoteHistory] = useState<Vote[] | []>([]);

  const [trackerData, setTrackerData] = useState<TrackingProfile | undefined>(undefined);

	const competitionDatesAllowVoting = () => {
		return (competitionHasStarted() && !competitionHasEnded());
	};

	const competitionHasStarted = () => {
		if (!competition.voting_open_date) {
			return true;
		};

		return (
			(competition.voting_open_date && (
				dayjs(competition.voting_open_date).isSame(dayjs(), 'date') ||
				dayjs().isAfter(dayjs(competition.voting_open_date), 'date')
			))
		);
	};

	const competitionHasEnded = () => {
		if (!competition.voting_close_date) {
			return false;
		};

		return (
			(competition.voting_close_date && (
				dayjs().isAfter(dayjs(competition.voting_close_date), 'date')
			))
		);
	};

	const votingRules = () => {
		//	not logged in
		if (trackerData?.email === undefined) {
			const loginReturnParams = `${competition.returnUrl ? 
				`?returnUrl=${competition.returnUrl}&returnText=${competition.returnText ?? 'Go back to voting'}` : 
				''}`;

			return {
				votingAllowed: false,
				status: false,
				message: `<a 
					href='/${process.env.MEMBER_CENTRE_SLUG}${loginReturnParams}' 
					class='text-danger'
				>You need to login before voting. Click here.</a>`,
			};
		}

		//	competition dates dictate voting closed
		if (!competitionHasStarted()) {
			return {
				votingAllowed: false,
				status: false,
				message: `Voting opens ${dayjs(competition.voting_open_date).format('MMMM D, YYYY')}`,
			};
		};

		if (competitionHasEnded()) {
			return {
				votingAllowed: false,
				status: false,
				message: `Voting closed ${dayjs(competition.voting_close_date).format('MMMM D, YYYY')}`,
			};
		};

		//	voting limit exceeded
		if (competition.total_votes_allowed > 0) {
			const votesToday = _.filter(voteHistory, (vote: Vote) => {
				return dayjs(vote.created).isSame(dayjs(), 'day');
			});
			if (
				_.isArray(votesToday) && 
				votesToday.length >= competition.total_votes_allowed
			) {
				return {
					votingAllowed: false,
					status: false,
					message: competition.voting_limit_reached_message,
				};
			}	
		}

		//	all good
		return {
			votingAllowed: true,
			status: undefined,
			message: '',
		};	
	};

	//	get email address from profile tracker
	useEffect(() => {
		const getTracker = async () => {
			const tracker = await getProfileTracker();
			setTrackerData(tracker);
			setVoterEmail(tracker.email);
			setVoterFirstName(tracker.firstname);
			setVoterLastName(tracker.lastname);
		};

		getTracker();
		return () => {};
	}, []);

	useEffect(() => {
		if (!competitionHasStarted()) {
			setBlockNotifications([
				{
					type: 'info',
					message: `Voting opens ${dayjs(competition.voting_open_date).format('MMMM D, YYYY')}`,
				},
			]);
		};

		if (competitionHasEnded()) {
			setBlockNotifications([
				{
					type: 'info',
					message: `Voting closed ${dayjs(competition.voting_close_date).format('MMMM D, YYYY')}`,
				},
			]);
		};
	}, []);

	useEffect(() => {
		setSubmitSuccessful(false);
	
		if (selectedEntry === undefined) {
			setVoteHistory([]);
			return () => {};
		};
	
		const getVotes = async () => {
			const votes = await get(`${endpoints.getCompetitionVotesByEmailAndSlug}?
				voter_email=${voterEmail}&
				competition_slug=${competition.competition_id}&
				entrant_email=${selectedEntry?.email}
			`);
			setVoteHistory(votes);
		};

		getVotes();
		setSubmitSuccessful(false);
		return () => {};
	}, [selectedEntry]);

	useEffect(() => {
		const rules = votingRules();
		setNotificationStatus(rules.status);
		setNotificationMessage(rules.message);
		setShowModal(selectedEntry !== undefined);
		// console.log('Voting rules useEffect', {selectedEntry, voteHistory, rules});
	}, [voteHistory]);

	const handleVoteSubmit = e => {
		e.preventDefault();
		setWasValidated(true);

		post(endpoints.postCompetitionVote, {
			voter_email: voterEmail, 
			competition_slug: competition.competition_id,
			entrant_email: selectedEntry?.email,
		})
		.catch(err => {
			// console.log(err);
			// trackEvent(SUBMIT_ERROR);
			setSubmitSuccessful(false);
			setNotificationStatus(false);
			setNotificationMessage('');
			setIsSaving(false);
			return Promise.reject(err);
		})
		.then((
			//@ts-ignore 
			data
		) => {
			// console.log(data);
			setSubmitSuccessful(true);
			setNotificationStatus(false);
			setNotificationMessage('');
			setBlockNotifications([
				{
					type: 'success',
					message: competition.successful_vote_message,
				},
			]);
			setSelectedEntry(undefined);
			setIsSaving(false);
		})
		.finally(() => {
			// trackEvent(SUBMIT_SUCCESS);
		});

		setIsSaving(true);
		return true;
	};

	return (
		<>
			<div className={styles.container} style={{}}>
				<Spacer row width={1160} align={'flex-start'}
					className={`entryBlocks ${styles.entryBlocks}`}>

					{blockNotifications.map((notification, i) => (
						<div
							key={i}
							className={`alert alert-${notification.type} text-center ${styles.alert}`}
							role="alert"
							dangerouslySetInnerHTML={{ __html: notification.message }}
						/>
					))}

					{entries.map((entry, i) => (
						<div
							key={i}
							className={cx(`entryCard lazy-load-bgimage`, styles.entryCard)}
							data-bgimage={`${entry.featured_image}?w=600`}
							style={{
								backgroundSize: `cover`,
								backgroundPosition: `center`,
							}}
						>
							<div
								className={cx(
									'entryContentContainer',
									styles.entryContentContainer
								)}
							>
								<div className={cx('entryTitle', styles.entryTitle)}>
									<a 
										href={'#'} 
										className={cx(
											'entryLink',
											styles.entryLink
										)}
										onClick={() => {
											setBlockNotifications([]);
											setSelectedEntry(entry);
										}}
									>
										{entry.first_name + ' ' + entry.last_name}
									</a>
								</div>
								<div
									className={cx('entryMetaContainer', styles.entryMetaContainer)}
								>
									<div className={cx(`entryMeta`, styles.entryMeta)}>
										<Button
											onClick={() => {
												setBlockNotifications([]);
												setSelectedEntry(entry);
											}}
										>
											{competition.voting_button_text}
										</Button>
									</div>
								</div>
							</div>
						</div>
					))}
				</Spacer>
			</div>

			{showModal && selectedEntry && (
        <Modal
          backgroundColor={colors.black}
          onClose={() => {
						setSelectedEntry(undefined);
						setShowModal(false);
					}}
          header={<h2>Vote for {selectedEntry.first_name} {selectedEntry.last_name}</h2>}
          size={'l'}
          footer={[
            {
              label: !competitionDatesAllowVoting() ?
								competition.voting_closed_message :
								isSaving ? 'Please wait' : `Give ${selectedEntry.first_name} my vote`,
              type: 'button',
              icon: {
								icon: !competitionDatesAllowVoting() || notificationStatus === false ? 'close' :
									isSaving ? 'autorenew' : 'thumb_up',
								rotateIcon: isSaving,
								className: 'mr-2',
							},
              disabled: isSaving || notificationStatus === false || !competitionDatesAllowVoting(),
              onClick: e => handleVoteSubmit(e),
            },
          ]}
        >
          <Spacer
            width={1160}
            space={'m'}
            align={'center'}
            justify={'center'}
          >
            {notificationStatus !== undefined && (
							<div
								className={`alert alert-${notificationStatus === true ? 'success' : 'danger'} text-center ${styles.alert}`}
								role="alert"
								dangerouslySetInnerHTML={{ __html: notificationMessage }}
							/>
            )}

						{selectedEntry && (
					    <Spacer width={1160} align={'flex-start'}>
								<div className={styles.entryDescription} dangerouslySetInnerHTML={{ __html: _.unescape(selectedEntry.description) }} />
								<div className={`w-100 text-center entryVideo ${styles.entryVideo}`}>
									<Video
											video_id={selectedEntry.youtube_video}
											bg_url={`${selectedEntry.featured_image}?w=600`}
											caption={undefined}
										/>
								</div>
							</Spacer>
						)}
          </Spacer>
        </Modal>
      )}
		</>
  );
};

export default compose(styled(competitionVoteStyles))(CompetitionVoteBlock);
